// ./src/components/features/EmailTemplates/EmailTemplatesPreviewModal/EmailTemplatesPreviewModal.tsx
import React, { useState, useEffect } from "react";
import { Stack, Text, Group, Badge, Skeleton, Box, Alert } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { EmailTemplate, isSystemTemplate } from "@/utils/api";
import { AppDrawer, StatusBadge } from "@/components/generic";

export interface EmailTemplatesPreviewModalProps {
  /** Whether the modal is open */
  opened: boolean;

  /** Function to close the modal */
  onClose: () => void;

  /** Email template to preview */
  emailTemplate?: EmailTemplate | null;

  /** Callback to fetch preview with rendered content */
  onPreviewEmailTemplate?: (
    id: string,
    sampleData?: string
  ) => Promise<EmailTemplate | null>;

  /** Whether preview operation is in progress */
  isPreviewing?: boolean;

  /** Last error from operations */
  lastError?: string | null;

  /** Callback to clear errors */
  onClearError?: () => void;
}

export const EmailTemplatesPreviewModal: React.FC<
  EmailTemplatesPreviewModalProps
> = ({
  opened,
  onClose,
  emailTemplate,
  onPreviewEmailTemplate,
  isPreviewing = false,
  lastError,
  onClearError,
}) => {
  const [previewData, setPreviewData] = useState<EmailTemplate | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);

  const isSystemTemplateItem = emailTemplate
    ? isSystemTemplate(emailTemplate)
    : false;

  // Fetch preview when modal opens
  useEffect(() => {
    if (opened && emailTemplate?.id && onPreviewEmailTemplate) {
      const fetchPreview = async () => {
        setIsLoadingPreview(true);
        setPreviewData(null);
        onClearError?.();

        try {
          const result = await onPreviewEmailTemplate(emailTemplate.id!);
          setPreviewData(result);
        } catch (error) {
          console.error("Failed to fetch preview:", error);
        } finally {
          setIsLoadingPreview(false);
        }
      };

      fetchPreview();
    }

    // Reset state when modal closes
    if (!opened) {
      setPreviewData(null);
      setIsLoadingPreview(false);
    }
  }, [opened, emailTemplate?.id, onPreviewEmailTemplate, onClearError]);

  const renderLoadingSkeleton = () => (
    <Stack gap="md">
      {/* Subject skeleton */}
      <Box>
        <Skeleton height={16} width={60} mb={8} />
        <Skeleton height={24} width="70%" />
      </Box>

      {/* Content skeleton */}
      <Box
        style={{
          border: "1px solid var(--mantine-color-gray-3)",
          borderRadius: "8px",
          padding: "16px",
          backgroundColor: "#fff",
        }}
      >
        <Skeleton height={32} width="60%" mb={16} />
        <Skeleton height={16} width="90%" mb={8} />
        <Skeleton height={16} width="85%" mb={8} />
        <Skeleton height={16} width="75%" mb={16} />
        <Skeleton height={24} width="40%" mb={12} />
        <Skeleton height={16} width="100%" mb={8} />
        <Skeleton height={16} width="95%" mb={8} />
        <Skeleton height={16} width="88%" mb={8} />
        <Skeleton height={16} width="92%" mb={16} />
        <Skeleton height={14} width="50%" mb={4} />
        <Skeleton height={14} width="45%" />
      </Box>
    </Stack>
  );

  const renderPreviewContent = () => {
    if (isLoadingPreview || isPreviewing) {
      return renderLoadingSkeleton();
    }

    if (lastError) {
      return (
        <Alert icon={<IconInfoCircle size={16} />} color="red" variant="light">
          {lastError}
        </Alert>
      );
    }

    if (!previewData) {
      return (
        <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
          No preview available. The template may not have any content yet.
        </Alert>
      );
    }

    return (
      <Stack gap="md">
        {/* Subject preview */}
        {previewData.subject && (
          <Box>
            <Text size="sm" fw={500} c="dimmed" mb={4}>
              Subject
            </Text>
            <Text size="md">{previewData.subject}</Text>
          </Box>
        )}

        {/* HTML content preview */}
        {previewData.htmlContent && (
          <Box>
            <Text size="sm" fw={500} c="dimmed" mb={8}>
              Email Content
            </Text>
            <Box
              style={{
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: "8px",
                padding: "16px",
                backgroundColor: "#fff",
                maxHeight: "500px",
                overflow: "auto",
              }}
              dangerouslySetInnerHTML={{ __html: previewData.htmlContent }}
            />
          </Box>
        )}

        {/* Plain text preview (if no HTML) */}
        {!previewData.htmlContent && previewData.textContent && (
          <Box>
            <Text size="sm" fw={500} c="dimmed" mb={8}>
              Email Content (Plain Text)
            </Text>
            <Box
              style={{
                border: "1px solid var(--mantine-color-gray-3)",
                borderRadius: "8px",
                padding: "16px",
                backgroundColor: "#fff",
                fontFamily: "monospace",
                fontSize: "13px",
                whiteSpace: "pre-wrap",
              }}
            >
              {previewData.textContent}
            </Box>
          </Box>
        )}
      </Stack>
    );
  };

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title="Email Template Preview"
      position="right"
      size="lg"
      closable={!isLoadingPreview}
      actionType="close"
      errors={lastError}
    >
      <Stack gap="md">
        {/* Template info header */}
        {emailTemplate && (
          <Box
            style={{
              padding: "12px 16px",
              backgroundColor: "var(--mantine-color-gray-0)",
              borderRadius: "8px",
            }}
          >
            <Group gap="xs" mb={4}>
              <Text fw={600} size="md">
                {emailTemplate.name || "Untitled Template"}
              </Text>
              {isSystemTemplateItem && (
                <Badge variant="light" color="blue" size="xs">
                  System
                </Badge>
              )}
              {emailTemplate.isDefault && (
                <Badge variant="light" color="green" size="xs">
                  Default
                </Badge>
              )}
            </Group>
            <Group gap="md">
              {emailTemplate.templateType && (
                <Text size="sm" c="dimmed">
                  Type:{" "}
                  <Text span fw={500} c="dark">
                    {emailTemplate.templateType.charAt(0).toUpperCase() +
                      emailTemplate.templateType.slice(1)}
                  </Text>
                </Text>
              )}
              {emailTemplate.status !== undefined && (
                <Group gap={4}>
                  <Text size="sm" c="dimmed">
                    Status:
                  </Text>
                  <StatusBadge status={emailTemplate.status} size="xs" />
                </Group>
              )}
            </Group>
            {emailTemplate.description && (
              <Text size="sm" c="dimmed" mt={8}>
                {emailTemplate.description}
              </Text>
            )}
          </Box>
        )}

        {/* Info about preview */}
        <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
          This preview shows how the email will appear with sample data. The
          actual email content will vary based on the form submission data.
        </Alert>

        {/* Preview content */}
        {renderPreviewContent()}
      </Stack>
    </AppDrawer>
  );
};
